import api from "@/core/api/axios";
import { AuthContext } from "@/shared/providers/AuthProvider/context";
import { useLocalStorage } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import log from "loglevel";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  APIResponseBFF,
  APIResponseBFFData,
} from "@superagents/types/api/api.v5";
import { getTrackingParams } from "@/shared/utils/trackingParams";
import {
  resetAnalytics,
  capture,
  setUserProperties,
  registerSuperProperties,
} from "@/shared/lib/analytics";
import { useIdentifyActiveCompany } from "@/shared/providers/AuthProvider/useIdentifyActiveCompany";
import { useRegisterOnboardingMode } from "@/shared/providers/AuthProvider/useRegisterOnboardingMode";
import { isRecentSignup } from "@/shared/utils/isRecentSignup";
import { resolveOnboardingVariant } from "@/shared/utils/onboardingVariant";
import {
  resolveOrigin,
  resolveSalesEntryCode,
  clearSalesEntry,
  resolvePromoCode,
  clearPromoCode,
} from "@/shared/utils/salesEntry";
import { clearOnboardingV2WizardStorage } from "@/shared/utils/onboardingV2Storage";
import {
  identifyForAnalytics,
  normalizeUserForOnboardingV2,
  userMeQueryOptions,
} from "@/shared/providers/AuthProvider/userMeQuery";
import { resetChatEventLedger } from "@/features/chat/realtime/chatEventLedger";
import { resetCoalescedInvalidations } from "@/features/chat/realtime/coalescedInvalidation";

// BFC P9 (client): registra `onboarding_mode` como super property para os
// eventos client carregarem o modo real (igual ao server). Precisa rodar DENTRO
// do AuthContext.Provider, não no corpo do AuthProvider — `useRegisterOnboardingMode`
// → `useSectorAccessState` → `useApiQuery` → `useAuth`, que lança se o contexto
// ainda não estiver montado. Por isso é um componente filho (renderiza null).
const OnboardingModeTracker: React.FC<{ sectorId?: string | null }> = ({
  sectorId,
}) => {
  useRegisterOnboardingMode(sectorId);
  return null;
};

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [authStorage, setAuthStorage, removeAuthStorage] = useLocalStorage<{
    token?: string;
  }>({
    key: "auth",
    defaultValue: { token: undefined },
    getInitialValueInEffect: false,
  });

  const logout = useCallback(() => {
    // SA-1452: o registro de conversas e as invalidações agendadas são da
    // sessão que sai; outra conta no mesmo navegador não herda nada disso.
    resetChatEventLedger();
    resetCoalescedInvalidations(queryClient);
    resetAnalytics();
    clearOnboardingV2WizardStorage();
    removeAuthStorage();
    queryClient.clear();
    navigate("/");
  }, [removeAuthStorage, navigate, queryClient]);

  const {
    data: user,
    isLoading: isUserLoading,
    isError,
  } = useQuery({
    // A chave depende do token (troca de login = outro usuário em cache).
    ...userMeQueryOptions(authStorage?.token),
    enabled: !!authStorage?.token, // Habilitado sempre que houver um token
  });

  // SA-1166: vincula o usuário à Company (PostHog Group) assim que o companyId
  // for conhecido — inclusive quando o onboarding grava o bffUser via
  // setQueryData (que não passa pelo queryFn acima). Seguro no corpo do
  // provider: não depende de `useAuth` (recebe o companyId por parâmetro).
  useIdentifyActiveCompany(user?.companyId);

  // SA-1217: conta que já tem Company nunca vai rodar o `init`, então nenhum dos
  // dois pontos de consumo do código do link de vendas dispara para ela. Sem isso o
  // valor fica no localStorage e vaza para o próximo cadastro feito no mesmo
  // browser. Cobre quem abre o link e entra numa conta existente (login local ou
  // Google recorrente) E quem abre o link já logado — este último não passa por
  // nenhum login. Não corre risco de limpar cedo: a Company nasce no próprio
  // `init`, então `companyId` só existe depois de o modo já ter sido consumido
  // (e um usuário não pode ter duas — o `init` rejeita com 409).
  useEffect(() => {
    if (user?.companyId) {
      clearSalesEntry();
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (isError) {
      logout();
    }
  }, [isError, logout]);

  const forgotPasswordMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await api().post("/auth/forgot-password", {
        email,
      });

      return response.data;
    },
    onError: (error) => {
      log.error("Failed to request password reset:", error);
      return { ok: false };
    },
  });

  const forgotPassword = useCallback(
    async (email: string) => {
      await forgotPasswordMutation.mutateAsync(email);
      return { ok: true };
    },
    [forgotPasswordMutation]
  );

  const resetPasswordMutation = useMutation({
    mutationFn: async ({
      code,
      password,
      passwordConfirmation,
    }: {
      code: string;
      password: string;
      passwordConfirmation: string;
    }) => {
      const response = await api().post("/auth/reset-password", {
        code,
        password,
        passwordConfirmation,
      });

      return response.data;
    },
    onError: (error) => {
      log.error("Failed to reset password:", error);
      return { ok: false };
    },
  });

  const resetPassword = useCallback(
    async (code: string, password: string, passwordConfirmation: string) => {
      await resetPasswordMutation.mutateAsync({
        code,
        password,
        passwordConfirmation,
      });
      return { ok: true };
    },
    [resetPasswordMutation]
  );

  const registerMutation = useMutation({
    mutationFn: async ({
      username,
      email,
      password,
    }: {
      username: string;
      email: string;
      password: string;
    }) => {
      const trackingParams = getTrackingParams();
      // SA-1217: o código do link de vendas viaja no register (mesmo rail do
      // `utm_params`) — é a fonte preferida, porque o lead pode confirmar o
      // e-mail em outro device e chegar ao onboarding sem o localStorage.
      // Resolve pelo storage E pela URL: é a MESMA resolução que a página de
      // signup usa para mostrar o selo, então nunca exibimos uma oferta que o
      // cadastro não transporta (storage indisponível é o caso).
      const salesEntryCode = resolveSalesEntryCode();
      // SA-1279: o cupom viaja pelo mesmo rail, e pelo mesmo motivo — o lead
      // pode confirmar o e-mail em outro device e chegar ao onboarding sem o
      // localStorage.
      const promoCode = resolvePromoCode();
      const response = await api().post("/auth/local/register", {
        username,
        email,
        password,
        ...(trackingParams ? { utm_params: trackingParams } : {}),
        ...(salesEntryCode ? { sales_entry_code: salesEntryCode } : {}),
        ...(promoCode ? { promo_code: promoCode } : {}),
      });

      return response.data;
    },
    // Consumo de uso único: só no SUCESSO (numa falha de rede/e-mail duplicado
    // o modo tem que sobreviver para a próxima tentativa). A partir daqui o
    // valor está no User e o `init` o lê de lá.
    onSuccess: () => {
      clearSalesEntry();
      clearPromoCode();
    },
  });

  const register = useCallback(
    async (email: string, password: string) => {
      const data = await registerMutation.mutateAsync({
        username: email,
        email,
        password,
      });

      return data?.user;
    },
    [registerMutation]
  );

  const loginMutation = useMutation({
    mutationFn: async ({
      identifier,
      password,
    }: {
      identifier: string;
      password: string;
    }) => {
      const response = await api().post("/auth/local", {
        identifier,
        password,
      });
      const jwt: string | undefined = response.data?.jwt;
      if (!jwt) {
        throw new Error("Failed to login: JWT not received");
      }
      return { jwt };
    },
    // MUDANÇA: O onSuccess agora é mais simples. Ele só lida com o token.
    onSuccess: ({ jwt }) => {
      setAuthStorage({ token: jwt });
    },
    onError: (error) => {
      log.error("Failed to login:", error);
      // Limpa qualquer estado que possa ter ficado sujo
      removeAuthStorage();
      queryClient.removeQueries({ queryKey: ["user"] });
    },
  });

  const loginWithGoogleMutation = useMutation({
    mutationFn: async (accessToken: string) => {
      // SA-1355: no Google o User nasce neste callback, sem passar pelo
      // register, então o código do link de vendas vai na query e o backend o
      // grava no User já no cadastro. Não consome o valor: numa conta nova o
      // init ainda o lê daqui, e a limpeza por Company existente cobre o resto.
      const params = new URLSearchParams({ access_token: accessToken });
      const salesEntryCode = resolveSalesEntryCode();
      if (salesEntryCode) params.set("sales_entry_code", salesEntryCode);

      const response = await api().get(
        `/auth/google/callback?${params.toString()}`
      );
      const jwt: string | undefined = response.data?.jwt;
      if (!jwt) {
        throw new Error("Falha no login com Google: JWT não recebido");
      }
      return { jwt };
    },
    onSuccess: ({ jwt }) => {
      setAuthStorage({ token: jwt });
    },
    onError: (error) => {
      log.error("Falha no login com Google:", error);
      removeAuthStorage();
      queryClient.removeQueries({ queryKey: ["user"] });
    },
  });

  const loginWithGoogle = useCallback(
    async (accessToken: string) => {
      const { jwt } = await loginWithGoogleMutation.mutateAsync(accessToken);

      const userData = await queryClient.fetchQuery<
        APIResponseBFFData<"bff::user">
      >({
        queryKey: ["user", jwt],
        queryFn: async () => {
          const response = await api().get<APIResponseBFF<"bff::user">>(
            "/user/me",
            { headers: { Authorization: `Bearer ${jwt}` } }
          );
          const data = response.data?.data;
          if (!data) throw new Error("Dados do usuário não encontrados");
          const normalizedUserData = normalizeUserForOnboardingV2(data);
          await identifyForAnalytics(normalizedUserData);
          return normalizedUserData;
        },
      });

      // SA-1165: no OAuth do Google, signup e 1º login acontecem no mesmo
      // callback — um `createdAt` recente indica conta nova (signup). Logins
      // recorrentes têm createdAt antigo e não disparam o evento.
      if (isRecentSignup(userData.createdAt)) {
        // SA-1160/1161: aloca a variante do experimento também no signup por
        // Google — espelha o fluxo de email (useSignUpController). Sem isso, o
        // `signup_completed` do Google e os eventos client-side seguintes do
        // usuário ficam sem a dimensão `onboarding_variant` do experimento.
        const onboardingVariant = resolveOnboardingVariant();
        // SA-1216: mesma dupla de properties para `origin` — antes do capture,
        // pelo mesmo motivo do fluxo por e-mail (o evento é o topo do funil por
        // pessoa e não carrega o group da Company).
        const origin = resolveOrigin();
        await setUserProperties({
          onboarding_variant: onboardingVariant,
          origin,
        });
        registerSuperProperties({
          onboarding_variant: onboardingVariant,
          origin,
        });
        capture("signup_completed", { method: "google" });
      }

      return userData;
    },
    [loginWithGoogleMutation, queryClient]
  );

  const login = useCallback(
    async (identifier: string, password: string) => {
      // 1. Executa a mutação e espera ela terminar
      const { jwt } = await loginMutation.mutateAsync({ identifier, password });

      // 2. Com o novo token, busca manualmente os dados do usuário
      // O invalidateQueries seria outra opção, mas o fetch é mais direto e nos dá os dados imediatamente.
      const userData = await queryClient.fetchQuery<
        APIResponseBFFData<"bff::user">
      >({
        queryKey: ["user", jwt], // Usa a mesma chave que o useQuery
        queryFn: async () => {
          const response = await api().get<APIResponseBFF<"bff::user">>(
            "/user/me",
            { headers: { Authorization: `Bearer ${jwt}` } }
          );
          const data = response.data?.data;
          if (!data) throw new Error("User data not found after login");
          const normalizedUserData = normalizeUserForOnboardingV2(data);
          await identifyForAnalytics(normalizedUserData);
          return normalizedUserData;
        },
      });

      // 3. Retorna os dados do usuário para quem chamou (o useLoginController)
      return userData;
    },
    [loginMutation, queryClient]
  );

  useEffect(() => {
    const apiInstance = api();
    const interceptor = apiInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      apiInstance.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  // Todos os handlers já são `useCallback`, então o objeto fica estável entre
  // renders sem novidade. Sem o memo, cada render do provider re-renderizava
  // todo consumidor de `useAuth` — e a lista de conversas consome um por item.
  const value = useMemo(
    () => ({
      register,
      forgotPassword,
      resetPassword,
      login,
      loginWithGoogle,
      logout,
      user,
      token: authStorage?.token,
      isLoggingIn: loginMutation.isPending,
      isUserLoading,
      isLoggedIn: Boolean(authStorage?.token && user),
    }),
    [
      register,
      forgotPassword,
      resetPassword,
      login,
      loginWithGoogle,
      logout,
      user,
      authStorage?.token,
      loginMutation.isPending,
      isUserLoading,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      <OnboardingModeTracker sectorId={user?.sectorId} />
      {children}
    </AuthContext.Provider>
  );
};
